/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2024, Zumper
 */

import { IntegratedTourType, getEnumValues } from '@zcode/enums'
import { useMemo } from 'react'
import { useGetTourIntegration } from '../../TourOptions/useGetTourIntegration'
import { useGetTourAvailability } from './useGetTourAvailability'

export const useGetAllAvailableTourDates = (
  entity: Parameters<typeof useGetTourAvailability>[0]
) => {
  const { fetching } = useGetTourIntegration(entity)
  const dates = getEnumValues(IntegratedTourType).reduce((dates, type) => {
    /**
     * biome-ignore lint/correctness/useHookAtTopLevel: Because IntegratedTourType on the line above is
     * a hard-coded constant, we know for sure that this reduce callback is going to be called exactly 3
     * times every render, in the same order each time.
     * So React's requirement that we always call the same hooks in the same order is met
     */
    const { providedTimesInterval } = useGetTourAvailability(entity, type, {
      mayMakeInitialFetch: false,
    })
    if (providedTimesInterval) {
      for (const date of providedTimesInterval.keys()) {
        dates.add(date)
      }
    }
    return dates
  }, new Set<ReturnType<Date['toDateString']>>())
  // biome-ignore lint/correctness/useExhaustiveDependencies: dates vs. Array.from(dates)...
  return useMemo(
    () => ({
      dates,
      fetching,
    }),
    [Array.from(dates).sort().join('/'), fetching]
  )
}
